import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Save, Package, Barcode, FileText, CalendarDays,
  Hash, Euro, Pencil, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import Barcode1D from 'react-barcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_COLORS = {
  activo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  vendido: 'bg-blue-100 text-blue-800 border-blue-300',
  reparacion: 'bg-amber-100 text-amber-800 border-amber-300',
};
const ESTADO_LABELS = {
  activo: 'Activo',
  vendido: 'Vendido',
  reparacion: 'En reparación',
};

export default function AccesorioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showLabel, setShowLabel] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/accesorios/${id}`);
        setItem(res.data);
        setForm(res.data);
      } catch {
        toast.error('Accesorio no encontrado');
        navigate('/accesorios');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.precio_compra) payload.precio_compra = parseFloat(payload.precio_compra);
      if (payload.año_modelo) payload.año_modelo = parseInt(payload.año_modelo);
      // Remove non-editable fields
      delete payload.codigo_interno;
      delete payload.codigo_barras;
      delete payload.id;
      delete payload.empresa_id;
      delete payload.created_at;
      delete payload.updated_at;

      const res = await axios.put(`${API}/accesorios/${id}`, payload);
      setItem(res.data);
      setForm(res.data);
      setEditing(false);
      toast.success('Accesorio actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    setShowLabel(true);
    setTimeout(() => {
      window.print();
      setShowLabel(false);
    }, 300);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (!item) return null;

  const Field = ({ label, icon: Icon, children }) => (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </Label>
      {children}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="accesorio-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/accesorios')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
                {item.nombre}
              </h1>
              <Badge className={`${ESTADO_COLORS[item.estado]} text-xs border`}>
                {ESTADO_LABELS[item.estado] || item.estado}
              </Badge>
            </div>
            <p className="text-sm text-slate-500 font-mono mt-0.5">{item.codigo_interno}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-label-btn">
            <Printer className="w-4 h-4 mr-1" /> Etiqueta
          </Button>
          {isAdmin && !editing && (
            <Button onClick={() => setEditing(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="edit-accesorio-btn">
              <Pencil className="w-4 h-4 mr-2" /> Editar
            </Button>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); setForm(item); }}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-accesorio-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-orange-500" /> Información General
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Código Interno" icon={Hash}>
              <Input value={item.codigo_interno} disabled className="bg-slate-50 font-mono font-semibold text-orange-600" data-testid="detail-codigo" />
            </Field>
            <Field label="Código de Barras" icon={Barcode}>
              <Input value={item.codigo_barras || ''} disabled className="bg-slate-50 font-mono" data-testid="detail-barcode" />
            </Field>
            <Field label="Nombre" icon={Package}>
              {editing ? (
                <Input value={form.nombre || ''} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="edit-nombre" />
              ) : (
                <p className="text-sm font-medium text-slate-900 py-2">{item.nombre}</p>
              )}
            </Field>
            <Field label="Tipo">
              {editing ? (
                <Input value={form.tipo || ''} onChange={e => setForm({ ...form, tipo: e.target.value })} data-testid="edit-tipo" />
              ) : (
                <p className="text-sm text-slate-700 py-2">{item.tipo || '—'}</p>
              )}
            </Field>
            <Field label="Nº Serie">
              {editing ? (
                <Input value={form.numero_serie || ''} onChange={e => setForm({ ...form, numero_serie: e.target.value })} data-testid="edit-serie" />
              ) : (
                <p className="text-sm text-slate-700 font-mono py-2">{item.numero_serie || '—'}</p>
              )}
            </Field>
            <Field label="Estado">
              {editing ? (
                <Select value={form.estado} onValueChange={v => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="edit-estado">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="vendido">Vendido</SelectItem>
                    <SelectItem value="reparacion">En reparación</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge className={`${ESTADO_COLORS[item.estado]} text-xs border w-fit`}>
                  {ESTADO_LABELS[item.estado]}
                </Badge>
              )}
            </Field>
          </CardContent>
        </Card>

        {/* Purchase / Financial */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Euro className="w-5 h-5 text-orange-500" /> Compra
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Nº Factura" icon={FileText}>
              {editing ? (
                <Input value={form.numero_factura || ''} onChange={e => setForm({ ...form, numero_factura: e.target.value })} data-testid="edit-factura" />
              ) : (
                <p className="text-sm text-slate-700 py-2">{item.numero_factura || '—'}</p>
              )}
            </Field>
            <Field label="Fecha Compra" icon={CalendarDays}>
              {editing ? (
                <Input type="date" value={form.fecha_compra || ''} onChange={e => setForm({ ...form, fecha_compra: e.target.value })} data-testid="edit-fecha" />
              ) : (
                <p className="text-sm text-slate-700 py-2">
                  {item.fecha_compra ? new Date(item.fecha_compra).toLocaleDateString('es-ES') : '—'}
                </p>
              )}
            </Field>
            <Field label="Año Modelo">
              {editing ? (
                <Input type="number" value={form.año_modelo || ''} onChange={e => setForm({ ...form, año_modelo: e.target.value })} data-testid="edit-year" />
              ) : (
                <p className="text-sm text-slate-700 py-2">{item.año_modelo || '—'}</p>
              )}
            </Field>
            <Field label="Precio Compra" icon={Euro}>
              {editing ? (
                <Input type="number" step="0.01" value={form.precio_compra || ''} onChange={e => setForm({ ...form, precio_compra: e.target.value })} data-testid="edit-precio" />
              ) : (
                <p className="text-sm font-semibold text-slate-900 py-2">
                  {item.precio_compra != null ? `${Number(item.precio_compra).toFixed(2)} €` : '—'}
                </p>
              )}
            </Field>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Notas</CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px]"
                value={form.notas || ''}
                onChange={e => setForm({ ...form, notas: e.target.value })}
                data-testid="edit-notas"
              />
            ) : (
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.notas || 'Sin notas'}</p>
            )}
          </CardContent>
        </Card>

        {/* Barcode visual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Barcode className="w-5 h-5 text-orange-500" /> Código de Barras
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            {item.codigo_barras ? (
              <Barcode1D value={item.codigo_barras} width={1.5} height={50} fontSize={12} />
            ) : (
              <p className="text-sm text-slate-400">Sin código</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Print label (hidden, shown on print) */}
      {showLabel && (
        <div className="print-only fixed inset-0 bg-white z-[9999] flex items-center justify-center">
          <div className="text-center p-6">
            <p className="font-bold text-lg mb-1">{item.nombre}</p>
            <p className="text-sm text-slate-500 mb-2">{item.tipo}</p>
            {item.codigo_barras && (
              <Barcode1D value={item.codigo_barras} width={2} height={60} fontSize={14} />
            )}
            <p className="mt-2 font-mono text-sm">{item.codigo_interno}</p>
          </div>
        </div>
      )}
    </div>
  );
}
